// M9.3 — /share/:token. Public, read-only report shell. No app chrome,
// no sidebar, no Connect/Settings/Exports, no property picker, no
// sign-out, no Download buttons. Meta robots=noindex is set here and on
// the JSON endpoint's X-Robots-Tag so scrapers and link previews can't
// cache the snapshot.

import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { SkedMark } from "@/components/SkedMark";
import { MetricCard } from "@/components/ui/metric-card";
import { Button } from "@/components/ui/button";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ExecutiveSummary } from "@/components/report/ExecutiveSummary";
import { PlatformLeaderboard } from "@/components/report/PlatformLeaderboard";
import { SectionHeader } from "@/components/report/SectionHeader";
import { LandingPageTable } from "@/components/report/LandingPageTable";
import { CountryTable } from "@/components/report/CountryTable";
import { ChoroplethSkeleton } from "@/components/report/ChoroplethSkeleton";
import { DateRangeChip } from "@/components/report/DateRangeChip";
import { SourceMixSection } from "@/components/report/SourceMixSection";
import { DepthSection } from "@/components/report/DepthSection";
import { MobileSection } from "@/components/report/MobileSection";
import { EcommerceSection } from "@/components/report/EcommerceSection";
import { FrozenInsightsSection } from "@/components/report/InsightsSection";
import { TimeSeriesSection } from "@/components/report/TimeSeriesSection";
import { inferGranularity } from "@/components/report/granularity";
import { formatDateRangeLabel } from "@shared/report/date-range";
import { buildSkedHref } from "@shared/leadgen/cta-rules";
import {
  engagementRateInfo,
  keyEventsInfo,
  revenueInfo,
  sessionsInfo,
} from "@/components/report/metric-info";
import {
  fetchSharedReport,
  type InsightsResult,
  type ReportCompare,
  type ReportPayload,
  type ReportRange,
  type SharedReportProperty,
  type InsightsV2,
} from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { cn } from "@/lib/utils";

const WorldChoropleth = lazy(() => import("@/components/report/WorldChoropleth"));

type Status =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "not_found" }
  | { kind: "expired"; expiresAt: number }
  | {
      kind: "ready";
      report: ReportPayload;
      property: SharedReportProperty;
      createdAt: number;
      // M13.6 — frozen AI insights baked into the v3 envelope at mint
      // time. Null on legacy v1/v2 envelopes (the viewer simply omits
      // the section). No regenerate button on the public surface.
      insights: InsightsResult | null;
      // SKE-3458 — V2 structured insights on v4 envelopes. When present,
      // FrozenInsightsSection renders the headline + preview + "See more
      // detail" disclosure + action cards (same layout as the owner's
      // dashboard). Null / undefined on older envelopes — falls back to
      // the legacy `insights` render path.
      insightsV2: InsightsV2 | null;
    };

export function SharedReportPage() {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<Status>({ kind: "loading" });

  useEffect(() => {
    // noindex for crawlers that don't honour the server-side X-Robots-Tag
    // (eg. link-preview bots that hit the SPA shell first, before the JSON
    // API). Also cleared on unmount so the same tab navigating onwards
    // doesn't leak the meta tag.
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setStatus({ kind: "not_found" });
      return;
    }
    let cancelled = false;
    setStatus({ kind: "loading" });
    (async () => {
      try {
        const result = await fetchSharedReport(token);
        if (cancelled) return;
        if (result.kind === "ok")
          setStatus({
            kind: "ready",
            report: result.report,
            property: result.property,
            createdAt: result.createdAt,
            insights: result.insights ?? null,
            insightsV2: result.insightsV2 ?? null,
          });
        else if (result.kind === "expired")
          setStatus({ kind: "expired", expiresAt: result.expiresAt });
        else setStatus({ kind: "not_found" });
      } catch (e) {
        if (cancelled) return;
        setStatus({ kind: "error", message: (e as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const property = status.kind === "ready" ? status.property : null;
  usePageMeta({
    title: property?.propertyName
      ? `Shared traffic report — ${property.propertyName} · Traffic Snapshot`
      : "Shared traffic report · Traffic Snapshot",
    description:
      "Read-only Traffic Snapshot report shared by a Sked Social customer. Snapshot data only — no login required.",
  });

  return (
    <TooltipProvider delayDuration={150} skipDelayDuration={300}>
      <div className="min-h-dvh bg-white text-ink antialiased">
        <ShareHeader property={property} />
        <main className="mx-auto max-w-[1160px] px-6 py-10 sm:px-10 sm:py-14">
          {renderBody(status)}
        </main>
        <ShareFooter />
      </div>
    </TooltipProvider>
  );
}

function renderBody(status: Status): ReactNode {
  if (status.kind === "loading") return <LoadingShell />;
  if (status.kind === "not_found") return <NotFoundPanel />;
  if (status.kind === "expired") return <ExpiredPanel expiresAt={status.expiresAt} />;
  if (status.kind === "error") return <ErrorPanel message={status.message} />;
  return (
    <SharedReport
      report={status.report}
      property={status.property}
      createdAt={status.createdAt}
      insights={status.insights}
      insightsV2={status.insightsV2}
    />
  );
}

// Nav linking the shared viewer to the public-friendly tools from the
// authenticated app. UTM Health Check is fully public; Builder and
// Taxonomy require sign-in — the link still works, auth-gating kicks in
// once the visitor clicks through.
const TOOLS_NAV: ReadonlyArray<{ label: string; to: string; icon: string }> = [
  { label: "Tag health", to: "/utm-health-check", icon: "fa-regular fa-shield-check" },
  { label: "UTM builder", to: "/app/builder", icon: "fa-regular fa-wrench" },
  { label: "Taxonomy", to: "/app/taxonomy", icon: "fa-regular fa-list-tree" },
];

const SHARE_VIEWER_HEADER_HREF = buildSkedHref({
  medium: "share_viewer",
  campaign: "try_sked",
  content: "header_button",
});
const SHARE_VIEWER_FOOTER_LINK_HREF = buildSkedHref({
  medium: "share_viewer",
  campaign: "try_sked",
  content: "footer_link",
});
const SHARE_VIEWER_FOOTER_BUTTON_HREF = buildSkedHref({
  medium: "share_viewer",
  campaign: "try_sked",
  content: "footer_button",
});

function ShareHeader({ property }: { property: SharedReportProperty | null }) {
  return (
    <header className="border-b border-neutral-200 bg-white">
      <div className="mx-auto flex max-w-[1160px] flex-col gap-4 px-6 py-4 sm:px-10 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <SkedMark className="h-7 w-auto" />
          <div className="flex flex-col leading-tight">
            <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-purple-700">
              Traffic Snapshot <span className="text-neutral-500">from Sked Social</span>
            </span>
            <span className="text-xs text-neutral-500">
              Shared report · read-only
              {property && property.propertyName !== "Shared report" ? (
                <>
                  {" · "}
                  <span className="font-medium text-neutral-700">{property.propertyName}</span>
                </>
              ) : null}
            </span>
          </div>
        </div>
        <nav className="flex flex-wrap items-center gap-1.5" aria-label="Related Sked tools">
          {TOOLS_NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-neutral-700 ring-1 ring-neutral-950/10 transition-colors hover:bg-neutral-50 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
            >
              <i className={`${item.icon} text-[11px] text-purple-600`} aria-hidden="true" />
              {item.label}
            </Link>
          ))}
          <a
            href={SHARE_VIEWER_HEADER_HREF}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full bg-purple-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm ring-1 ring-purple-600 transition-colors hover:bg-purple-700 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
          >
            Try Sked Social
            <i
              className="fa-regular fa-arrow-up-right-from-square text-[10px]"
              aria-hidden="true"
            />
          </a>
        </nav>
      </div>
    </header>
  );
}

function ShareFooter() {
  return (
    <footer className="mt-16 border-t border-neutral-200 bg-neutral-50">
      <div className="mx-auto flex max-w-[1160px] flex-col items-start gap-3 px-6 py-8 sm:flex-row sm:items-center sm:justify-between sm:px-10">
        <p className="text-sm text-neutral-600">
          Made with{" "}
          <Link to="/" className="font-semibold text-purple-700 underline-offset-2 hover:underline">
            Sked Social Traffic Snapshot
          </Link>
          {" — "}
          <a
            href={SHARE_VIEWER_FOOTER_LINK_HREF}
            target="_blank"
            rel="noreferrer"
            className="underline-offset-2 hover:underline"
          >
            from Sked Social
          </a>
          .
        </p>
        <Button asChild>
          <a href={SHARE_VIEWER_FOOTER_BUTTON_HREF} target="_blank" rel="noreferrer">
            Try Sked Social →
          </a>
        </Button>
      </div>
    </footer>
  );
}

function LoadingShell() {
  return (
    <div className="space-y-10" aria-busy="true">
      <div className="h-10 w-64 animate-pulse rounded-md bg-neutral-100" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-28 animate-pulse rounded-xl bg-neutral-100" />
        ))}
      </div>
      <div className="h-[240px] animate-pulse rounded-2xl bg-neutral-100" />
    </div>
  );
}

function NotFoundPanel() {
  return (
    <section className="mx-auto max-w-lg rounded-2xl bg-white p-10 text-center ring-1 ring-neutral-950/5">
      <i className="fa-regular fa-link-slash text-3xl text-neutral-400" aria-hidden="true" />
      <h1 className="mt-4 font-display text-2xl font-semibold text-neutral-950">
        This share link doesn't exist
      </h1>
      <p className="mt-2 text-sm text-neutral-600">
        It may have been revoked or mistyped. Check with whoever sent you the link.
      </p>
      <div className="mt-6">
        <Button asChild>
          <Link to="/">Go to Sked Social Traffic Snapshot</Link>
        </Button>
      </div>
    </section>
  );
}

function ExpiredPanel({ expiresAt }: { expiresAt: number }) {
  return (
    <section className="mx-auto max-w-lg rounded-2xl bg-white p-10 text-center ring-1 ring-neutral-950/5">
      <i className="fa-regular fa-clock text-3xl text-amber-500" aria-hidden="true" />
      <h1 className="mt-4 font-display text-2xl font-semibold text-neutral-950">
        This share link has expired
      </h1>
      <p className="mt-2 text-sm text-neutral-600">
        The owner set a lifetime that ended on{" "}
        <strong className="font-semibold text-neutral-800">
          {new Date(expiresAt).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        </strong>
        . Ask them to mint a fresh link if you still need this report.
      </p>
      <div className="mt-6">
        <Button asChild>
          <Link to="/?utm_source=traffic-snapshot&utm_medium=share-expired&utm_campaign=share-viewer">
            See how Traffic Snapshot works
          </Link>
        </Button>
      </div>
    </section>
  );
}

function ErrorPanel({ message }: { message: string }) {
  return (
    <section className="mx-auto max-w-lg rounded-2xl bg-white p-10 text-center ring-1 ring-neutral-950/5">
      <i className="fa-regular fa-triangle-exclamation text-3xl text-red-500" aria-hidden="true" />
      <h1 className="mt-4 font-display text-2xl font-semibold text-neutral-950">
        Something went wrong
      </h1>
      <p className="mt-2 text-sm text-neutral-600">{message}</p>
    </section>
  );
}

function SharedReport({
  report,
  property,
  createdAt,
  insights,
  insightsV2,
}: {
  report: ReportPayload;
  property: SharedReportProperty;
  createdAt: number;
  insights: InsightsResult | null;
  insightsV2: InsightsV2 | null;
}) {
  const { overview, perPlatform, perCountry, landingPages, recommendations, meta, utmHealth } =
    report;
  const showRevenue = overview.totalRevenue > 0;
  const compareActive = meta.compare !== "none";
  const hasPropertyName = property.propertyName && property.propertyName !== "Shared report";
  const snapshotDate = new Date(createdAt).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <div className="space-y-10">
      <header>
        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-purple-700">
          Traffic report
        </p>
        <span aria-hidden="true" className="mt-1.5 block h-[3px] w-8 rounded-full bg-purple-500" />
        <h1 className="mt-3 font-display text-3xl font-semibold text-neutral-950 sm:text-4xl">
          Social traffic <em className="font-serif text-purple-700">at a glance</em>
          {hasPropertyName ? (
            <span className="block text-lg font-semibold text-neutral-600 sm:text-xl">
              for <span className="text-neutral-900">{property.propertyName}</span>
            </span>
          ) : null}
        </h1>
        {/* M12.3 — shared report locks to the range + compare it was minted
            with. DateRangeChip shows the resolved ISO window (plus compare
            clause) so the viewer sees the same label the owner saw when they
            created the link, regardless of preset vs. custom. */}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <DateRangeChip
            startDate={meta.periodStart}
            endDate={meta.periodEnd}
            compare={meta.compare}
            comparePeriodStart={meta.comparePeriodStart}
            comparePeriodEnd={meta.comparePeriodEnd}
          />
          <span className="text-xs text-neutral-500">Snapshot taken {snapshotDate}</span>
        </div>
      </header>

      <section
        className={
          showRevenue
            ? "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
            : "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        }
      >
        <MetricCard
          state="data"
          label="Sessions"
          value={overview.sessions}
          delta={compareActive ? overview.deltas.sessions : null}
          info={sessionsInfo}
        />
        <MetricCard
          state="data"
          label="Engagement rate"
          value={overview.engagementRate}
          format={percent}
          info={engagementRateInfo}
        />
        <MetricCard
          state="data"
          label="Conversions"
          value={overview.keyEvents}
          delta={compareActive ? overview.deltas.keyEvents : null}
          info={keyEventsInfo(meta.propertyId)}
        />
        {showRevenue ? (
          <MetricCard
            state="data"
            label="Revenue"
            value={overview.totalRevenue}
            delta={compareActive ? overview.deltas.totalRevenue : null}
            format={currency}
            info={revenueInfo}
          />
        ) : null}
      </section>

      {/* SKE-3458 — trend chart (TimeSeriesSection) pulled into the
          shared viewer so it mirrors the dashboard's render order
          (KPIs → trend → sourceMix → depth → insights → actions →
          platforms → …). Uses inferred granularity since the shared
          viewer has no granularity control — the owner's window is
          already frozen into the envelope. */}
      {report.trend.length > 0 ? (
        <TimeSeriesSection
          trend={report.trend}
          customConversionsConfigured={overview.customConversions !== undefined}
          showRevenue={showRevenue}
          granularity={inferGranularity(meta.periodStart, meta.periodEnd)}
        />
      ) : null}

      {/* M13.1 — render the frozen sourceMix block when the envelope
          carries one (envelope schemaVersion stays v2 for now; legacy
          v1/v2 shares minted before M13.1 simply omit the block and we
          render nothing). */}
      {report.sourceMix && report.sourceMix.perChannel.length > 0 ? (
        <SourceMixSection
          sourceMix={report.sourceMix}
          compare={meta.compare}
          granularity={inferGranularity(meta.periodStart, meta.periodEnd)}
        />
      ) : null}

      {/* M13.2 — Depth section. Renders for any envelope whose trend
          rows carry the depth metrics (M13.2+ payloads). Pre-M13.2
          envelopes lack pageviews on trend rows; we silently skip the
          section in that case so legacy shares keep rendering. */}
      {report.trend.length > 0 && hasDepthMetrics(report.trend) ? (
        <DepthSection
          trend={report.trend}
          granularity={inferGranularity(meta.periodStart, meta.periodEnd)}
        />
      ) : null}

      {/* SKE-3458 — Frozen AI insights. v4 envelopes carry structured
          `insightsV2` (headline + preview bullets + "See more detail"
          disclosure + action cards — same layout as the dashboard).
          v3 envelopes fall back to legacy paragraph + bullets. v1/v2
          and failure-at-mint cases render nothing. No regenerate
          button on the public surface. */}
      <FrozenInsightsSection
        insights={insights}
        insightsV2={insightsV2}
        startDate={meta.periodStart}
        endDate={meta.periodEnd}
      />

      <ReportSection
        eyebrow="What to do next"
        title={
          <>
            Important <em className="font-serif text-purple-700">actions</em> to consider
          </>
        }
      >
        <ExecutiveSummary recommendations={recommendations} />
      </ReportSection>

      <ReportSection
        id="platforms"
        eyebrow="Platforms"
        title={
          <>
            Where <em className="font-serif text-purple-700">social traffic</em> is coming from
          </>
        }
      >
        <PlatformLeaderboard
          rows={perPlatform}
          compare={meta.compare}
          showRevenue={showRevenue}
          propertyId={meta.propertyId}
          ecommercePerPlatform={
            report.ecommerce?.windowActive ? report.ecommerce.perPlatform : undefined
          }
          currencyCode={meta.currencyCode ?? null}
        />
      </ReportSection>

      {/* M13.3 — Mobile section. Renders only when the envelope carries
          a mobile block (M13.3+ shares). Pre-M13.3 envelopes silently
          skip — same graceful-degradation pattern depth uses. */}
      {report.mobile && report.mobile.perDevice.length > 0 ? (
        <MobileSection
          mobile={report.mobile}
          granularity={inferGranularity(meta.periodStart, meta.periodEnd)}
          showRevenue={showRevenue}
          currencyCode={meta.currencyCode}
        />
      ) : null}

      <ReportSection
        id="landing-pages"
        eyebrow="Landing pages"
        title={
          <>
            Pages winning the <em className="font-serif text-purple-700">click</em>
          </>
        }
      >
        <LandingPageTable
          rows={landingPages}
          propertyLabel="shared report"
          periodLabel={formatDateRangeLabel(meta.periodStart, meta.periodEnd)}
          propertyId={meta.propertyId}
        />
      </ReportSection>

      {/* M13.4 — Ecommerce section. Renders frozen in the share envelope
          when the originating build had probe-pass + windowActive.
          Pre-M13.4 envelopes silently skip — same graceful-degradation
          pattern as the M13.1/2/3 sections. */}
      {report.ecommerce && report.ecommerce.windowActive ? (
        <EcommerceSection
          ecommerce={report.ecommerce}
          currencyCode={meta.currencyCode ?? null}
          granularity={inferGranularity(meta.periodStart, meta.periodEnd)}
          compareActive={meta.compare !== "none"}
        />
      ) : null}

      <ReportSection
        id="geography"
        eyebrow="Geography"
        title={
          <>
            Where in the <em className="font-serif text-purple-700">world</em>
          </>
        }
      >
        {perCountry.length === 0 ? (
          <CountryTable rows={perCountry} propertyId={meta.propertyId} />
        ) : (
          <div className="space-y-4">
            <Suspense fallback={<ChoroplethSkeleton />}>
              <WorldChoropleth rows={perCountry} />
            </Suspense>
            <CountryTable rows={perCountry} propertyId={meta.propertyId} />
          </div>
        )}
      </ReportSection>

      <ReportSection
        id="utm-health"
        eyebrow="UTM health"
        title={
          <>
            How <em className="font-serif text-purple-700">well-tagged</em> the traffic is
          </>
        }
      >
        <UtmHealthSummary score={utmHealth} />
      </ReportSection>
    </div>
  );
}

interface ReportSectionProps {
  id?: string;
  eyebrow: string;
  title: ReactNode;
  children: ReactNode;
}

function ReportSection({ id, eyebrow, title, children }: ReportSectionProps) {
  return (
    <section id={id} className="scroll-mt-24 space-y-4">
      <SectionHeader eyebrow={eyebrow} title={title} />
      {children}
    </section>
  );
}

function UtmHealthSummary({ score }: { score: ReportPayload["utmHealth"] }) {
  const bandBar: Record<ReportPayload["utmHealth"]["band"], string> = {
    excellent: "bg-green-500",
    good: "bg-green-400",
    "needs-work": "bg-yellow-500",
    broken: "bg-red-500",
  };
  const bandChip: Record<ReportPayload["utmHealth"]["band"], string> = {
    excellent: "bg-green-50 text-green-700",
    good: "bg-green-50 text-green-700",
    "needs-work": "bg-yellow-50 text-yellow-700",
    broken: "bg-red-50 text-red-700",
  };
  const bandLabel: Record<ReportPayload["utmHealth"]["band"], string> = {
    excellent: "Excellent",
    good: "Good",
    "needs-work": "Needs work",
    broken: "Broken",
  };
  return (
    <section className="rounded-2xl bg-white p-7 ring-1 ring-neutral-950/5">
      <div className="flex flex-wrap items-baseline justify-between gap-6">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-neutral-500">
            {score.totals.rows.toLocaleString()} tagged URLs ·{" "}
            {score.totals.sessions.toLocaleString()} sessions
          </p>
          <span
            className={cn(
              "mt-2 inline-flex rounded-full px-2.5 py-1.5 text-[13px] font-bold",
              bandChip[score.band],
            )}
          >
            {bandLabel[score.band]}
          </span>
        </div>
        <p className="font-display text-[64px] font-[900] leading-none tracking-[-0.03em] text-neutral-950 tabular-nums">
          {score.score}
          <span className="ml-1 text-2xl font-semibold text-neutral-400">/100</span>
        </p>
      </div>
      <div aria-hidden="true" className="mt-5 h-2 overflow-hidden rounded-full bg-neutral-100">
        <div
          className={cn("h-full rounded-full w-(--progress)", bandBar[score.band])}
          style={{ "--progress": `${score.score}%` } as React.CSSProperties}
        />
      </div>
    </section>
  );
}

function percent(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

function currency(n: number): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

// M13.2 — pre-M13.2 share envelopes don't carry pageviews on trend rows.
// Sniff the first row to decide whether to render the DepthSection so
// legacy shares stay clean. Numeric `0` is a valid value (zero traffic
// in window) so we test for `typeof === "number"` rather than truthiness.
function hasDepthMetrics(trend: ReportPayload["trend"]): boolean {
  const first = trend[0];
  if (!first) return false;
  return (
    typeof first.pageviews === "number" &&
    typeof first.activeUsers === "number" &&
    typeof first.totalUsers === "number"
  );
}

// Re-export only for type narrowing consumers; the public export stays the page.
export type { ReportCompare, ReportRange };
